import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Pagination,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import UserService from '../../services/user.service';
import TenantService from '../../services/tenant.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';

const TenantUsersList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const canViewAllTenants = hasPermission('users.view_all_tenants');
      const [usersRes, tenantsRes] = await Promise.all([
        canViewAllTenants ? UserService.getAllTenantUsers() : UserService.getAll(),
        TenantService.getAll({ per_page: 9999 })
      ]);

      const allUsers = [];
      if (usersRes.data) {
        const usersData = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.data || [];
        allUsers.push(...usersData);
      }
      setUsers(allUsers);

      const tenantsData = tenantsRes.data?.data ?? tenantsRes.data ?? [];
      setTenants(Array.isArray(tenantsData) ? tenantsData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar(t('tenantUsers.errorLoadingData'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleSendWelcomeEmail = async (user) => {
    handleMenuClose();
    if (!user?.id) return;
    try {
      await UserService.sendWelcomeEmail(user.id);
      showSnackbar(t('userManagement.welcomeEmailSentSuccess'));
    } catch (error) {
      console.error('Error sending welcome email:', error);
      showSnackbar(error.response?.data?.message || t('userManagement.failedSendWelcomeEmail'), 'error');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('tenantUsers.confirmDelete'))) {
      return;
    }

    try {
      await UserService.delete(userId);
      showSnackbar(t('tenantUsers.userDeletedSuccess'));
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      showSnackbar(t('tenantUsers.errorDeletingUser'), 'error');
    } finally {
      handleMenuClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      !searchTerm ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTenant = !tenantFilter || user.tenant_id?.toString() === tenantFilter;
    
    return matchesSearch && matchesTenant;
  });

  const paginatedUsers = filteredUsers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="bold">
              {t('tenantUsers.title')}
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              {hasPermission('users.create') && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => navigate(`/admin/users?add=1&returnTo=tenant-users${tenantFilter ? `&tenant_id=${tenantFilter}` : ''}`)}
                >
                  {t('tenantUsers.addUser')}
                </Button>
              )}
              <IconButton onClick={loadData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Filters */}
          <Box display="flex" gap={2} mb={3}>
            <TextField
              placeholder={t('tenantUsers.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('tenantUsers.filterByTenant')}</InputLabel>
              <Select
                value={tenantFilter}
                onChange={(e) => {
                  setTenantFilter(e.target.value);
                  setPage(1);
                }}
                label={t('tenantUsers.filterByTenant')}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id.toString()}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Alert severity="info">{t('tenantUsers.noUsersFound')}</Alert>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('tenantUsers.name')}</TableCell>
                      <TableCell>{t('tenantUsers.email')}</TableCell>
                      <TableCell>{t('tenantUsers.tenant')}</TableCell>
                      <TableCell>{t('tenantUsers.roles')}</TableCell>
                      <TableCell>{t('tenantUsers.status')}</TableCell>
                      <TableCell>{t('tenantUsers.lastLogin')}</TableCell>
                      <TableCell>{t('tenantUsers.createdAt')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon color="action" />
                            <Typography variant="body2">{user.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmailIcon color="action" fontSize="small" />
                            <Typography variant="body2">{user.email}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <BusinessIcon color="action" fontSize="small" />
                            <Typography variant="body2">
                              {user.tenant?.name || user.tenant_id || '-'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {user.roles?.map((role) => (
                              <Chip
                                key={typeof role === 'object' ? role.id : role}
                                label={typeof role === 'object' ? role.name : role}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                            {(!user.roles || user.roles.length === 0) && (
                              <Typography variant="caption" color="text.secondary">
                                {t('common.none')}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? t('common.active') : t('common.inactive')}
                            color={user.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.last_login_at ? formatDate(user.last_login_at) : t('tenantUsers.never')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(user.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('common.actions')}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, user)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}

              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  {t('tenantUsers.showingResults', {
                    from: (page - 1) * rowsPerPage + 1,
                    to: Math.min(page * rowsPerPage, filteredUsers.length),
                    total: filteredUsers.length
                  })}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {hasPermission('users.edit') && (
          <MenuItem onClick={() => {
            handleMenuClose();
            if (selectedUser?.id) {
              navigate('/admin/users', { state: { editUser: selectedUser, returnTo: 'tenant-users' } });
            }
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.edit')}</ListItemText>
          </MenuItem>
        )}
        {hasPermission('users.edit') && (
          <MenuItem onClick={() => handleSendWelcomeEmail(selectedUser)}>
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('userManagement.sendWelcomeEmail')}</ListItemText>
          </MenuItem>
        )}
        {hasPermission('users.delete') && (
          <MenuItem onClick={() => {
            handleDelete(selectedUser?.id);
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantUsersList;
